import React from "react";
import { ArrowLeft, ArrowRight, ArrowUp, ArrowDown, Home } from "lucide-react";

let googleMapsPromise = null;

export const loadGoogleMapsScript = (apiKey) => {
    if (googleMapsPromise) return googleMapsPromise;

    googleMapsPromise = new Promise((resolve, reject) => {
        if (window.google && window.google.maps && window.google.maps.places) {
            resolve();
            return;
        }

        // Check if script already exists in document
        const existingScript = document.querySelector(`script[src*="maps.googleapis.com/maps/api/js"]`);
        if (existingScript) {
            // If script exists but google is not ready, wait for it
            const checkReady = setInterval(() => {
                if (window.google && window.google.maps && window.google.maps.places) {
                    clearInterval(checkReady);
                    resolve();
                }
            }, 100);
            return;
        }

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
            // Double check that places library is actually available
            const checkPlaces = setInterval(() => {
                if (window.google && window.google.maps && window.google.maps.places) {
                    clearInterval(checkPlaces);
                    resolve();
                }
            }, 50);
        };
        script.onerror = () => {
            googleMapsPromise = null;
            reject(new Error("Failed to load Google Maps script"));
        };
        document.head.appendChild(script);
    });

    return googleMapsPromise;
};

export const highlightMatch = (text, matches) => {
    if (!matches || matches.length === 0) return text;

    let lastIndex = 0;
    const parts = [];

    // Sort matches by offset just in case
    const sortedMatches = [...matches].sort((a, b) => a.offset - b.offset);

    sortedMatches.forEach((match, i) => {
        // Add plain text before match
        if (match.offset > lastIndex) {
            parts.push(text.substring(lastIndex, match.offset));
        }
        // Add bolded match
        parts.push(
            <span key={i} className="font-extrabold text-blue-700">
                {text.substring(match.offset, match.offset + match.length)}
            </span>
        );
        lastIndex = match.offset + match.length;
    });

    // Add remaining text
    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }

    return parts;
};

export const getRelationDetails = (relation) => {
    switch (relation) {
        case 'left':
            return {
                label: '← Left Side',
                icon: ArrowLeft,
                bgColor: 'bg-emerald-50 border-emerald-200',
                textColor: 'text-emerald-600',
                badgeColor: 'bg-emerald-50 text-emerald-700 border-emerald-200'
            };
        case 'right':
            return {
                label: 'Right Side →',
                icon: ArrowRight,
                bgColor: 'bg-indigo-50 border-indigo-200',
                textColor: 'text-indigo-600',
                badgeColor: 'bg-indigo-50 text-indigo-700 border-indigo-200'
            };
        case 'front':
            return {
                label: '↑ Across Street',
                icon: ArrowUp,
                bgColor: 'bg-amber-50 border-amber-200',
                textColor: 'text-amber-600',
                badgeColor: 'bg-amber-50 text-amber-700 border-amber-200'
            };
        case 'behind':
            return {
                label: '↓ Behind / Rear',
                icon: ArrowDown,
                bgColor: 'bg-rose-50 border-rose-200',
                textColor: 'text-rose-600',
                badgeColor: 'bg-rose-50 text-rose-700 border-rose-200'
            };
        default:
            return {
                label: 'Neighbour',
                icon: Home,
                bgColor: 'bg-slate-50 border-slate-200',
                textColor: 'text-slate-600',
                badgeColor: 'bg-slate-50 text-slate-700 border-slate-200'
            };
    }
};

// Helper to calculate distance in meters
export const getDistanceMeters = (loc1, loc2) => {
    if (!loc1 || !loc2) return 999999;
    const dy = loc1.lat() - loc2.lat();
    const dx = loc1.lng() - loc2.lng();
    return Math.sqrt(dy * dy + dx * dx) * 111000;
};

// Helper to parse street address components
export const parseAddress = (addr) => {
    if (!addr) return null;
    const clean = addr.replace(/\s+(?:apt|unit|ste|#|apartment|suite|lot)\s+\S+/gi, '');
    const match = clean.match(/^(\d+)\s+(.+?),\s*(.+)$/);
    if (match) {
        return {
            houseNum: parseInt(match[1], 10),
            street: match[2].trim(),
            cityStateZip: match[3].trim()
        };
    }
    return null;
};

// Helper to extract address from ArcGIS attributes
export const getAddressFromAttributes = (attrs) => {
    if (!attrs) return null;
    const candidates = [
        attrs.Situs_Address_Full,
        attrs.SitusAddressFull,
        attrs.AGOL_Situs_Search,
        attrs.situs_address,
        attrs.SITUS_ADDRESS,
        attrs.Situs_Address,
        attrs.SitusAddress,
        attrs.situsaddr,
        attrs.Situs,
        attrs.situs,
        attrs.SITUSADDR,
        attrs.FULL_ADDRESS,
        attrs.FullAddress,
        attrs.Mail_Address_Full,
        attrs.AGOL_Situs_Address
    ];
    for (const val of candidates) {
        if (val && typeof val === 'string' && val.trim().length > 5) {
            return val.trim();
        }
    }
    return null;
};

// Helper to extract APN
export const getApnFromAttributes = (attrs) => {
    if (!attrs) return null;
    const candidates = [
        attrs.APN,
        attrs.apn,
        attrs.APN_S,
        attrs.apns,
        attrs.parcelnumb,
        attrs.PARCEL_NUM,
        attrs.ParcelNumber,
        attrs.ParcelNum,
        attrs.PIN,
        attrs.pin,
        attrs.prop_id,
        attrs.PID,
        attrs.geoID,
        attrs.geo_id,
        attrs.OBJECTID,
        attrs.OBJECTID_1
    ];
    for (const val of candidates) {
        if (val !== undefined && val !== null) {
            return String(val).trim();
        }
    }
    return null;
};

// Helper to convert to title case
export const toTitleCase = (str) => {
    if (!str) return '';
    return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};
